import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    viewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    AppNotificationService,
    AppNotificationType,
    assignModelOptions,
    CONSUME_CODE,
    CseStockService,
    EventService,
    MATERIAL_SOURCE_CODE,
    MaterialBillableChangeableModel,
    MaterialBillingIndicatorModel,
    MaterialModel,
    MaterialService,
    ConfigInstanceToken,
    ConfigInterface,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, of, Subject } from 'rxjs';
import { catchError, exhaustMap, take, takeUntil, tap } from 'rxjs/operators';
import ConfirmationMaterialStockModel from './confirmation-material-stock.model';
import { MaterialStockService } from '../../../material/services/material-stock-service';
import { SmartlockerService } from '../../../smartlocker/services/smartlocker.service';
import { TabsetComponent } from 'ngx-bootstrap/tabs';

export enum MatStockTab {
    BOOT_STOCK = 0,
    OTHER_STOCK = 1,
}

@Component({
    selector: 'pace-confirmation-material-stock',
    templateUrl: './confirmation-material-stock.component.html',
    styleUrls: ['./confirmation-material-stock.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ConfirmationMaterialStockComponent implements OnInit, OnDestroy {
    @Input()
    public model: ConfirmationMaterialStockModel;

    @Input()
    public isBootStockFromSideMenu = false;

    private readonly tabSet = viewChild<TabsetComponent>('tabSet');
    public isReverse = false;
    public orderField = '';
    public readonly MatStockTab = MatStockTab;
    public activeTab = MatStockTab.BOOT_STOCK;
    public isSearchInProgress = false;
    public isBootStockLoading = false;
    public isPickupInProgress = false;
    public confirmationMaterialStockFormChildren: FormArray;
    public confirmationMaterialStockForm: FormGroup;
    /** Cached result of getFilteredMaterials(), refreshed in validateForm() so the OnPush template doesn't re-filter model.bootStock on every CD cycle */
    public filteredBootStock: MaterialModel[] = [];

    /** Mandatory flag from the other-stock screen config; it only applies to the online search, not to boot stock filtering */
    private isMaterialNumberRequiredForSearch = false;
    private pickUpMaterialStock$ = new Subject<MaterialModel[]>();
    private destroy$ = new Subject<void>();

    public constructor(
        public readonly modalInstance: BsModalRef,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        private readonly translate: TranslateService,
        private readonly appNotificationService: AppNotificationService,
        private readonly materialService: MaterialService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly cseStockService: CseStockService,
        private readonly formBuilder: FormBuilder,
        private readonly changeDetector: ChangeDetectorRef,
        private eventService: EventService,
        private readonly materialStockService: MaterialStockService,
        private readonly smartlockerService: SmartlockerService,
    ) {}

    public ngOnInit(): void {
        this.confirmationMaterialStockFormChildren = this.formBuilder.array([]);
        this.confirmationMaterialStockForm = this.formBuilder.group({
            formChildren: this.confirmationMaterialStockFormChildren,
        });

        this.isMaterialNumberRequiredForSearch = !!this.model.materialNumber.options.required;
        this.applyMaterialNumberRequirement();

        if (this.isBootStockFromSideMenu) {
            this.filteredBootStock = this.getFilteredMaterials(this.model.bootStock);
        } else {
            this.loadBootStock();
        }

        this.pickUpMaterialStock$
            .asObservable()
            .pipe(
                take(1),
                exhaustMap((pickedMaterials) => {
                    const materials = pickedMaterials.map((material) => {
                        material.deliveredQuantity = material.deliveredQuantity - (material.quantity.value ?? 0);
                        material.maxQuantity = material.quantity.value ?? 0;
                        material.quantity = assignModelOptions(
                            material.quantity,
                            {
                                ...material.quantity.options,
                                minValue: 1, // For material consumption screen, minimum quantity value should be 1
                                maxValue: material.quantity.value,
                            },
                            material,
                        );
                        // important as we need to update the consumption code values
                        material.setConsumptionCodes();
                        // if cse stock material, Return option needs to be set based on one consumption code (Consumed)
                        material.setReturnTypeOptions(CONSUME_CODE.CONSUMED);
                        material.activityDate.value = moment();
                        if (material.materialSource.sourceCode === MATERIAL_SOURCE_CODE.BOOTSTOCK) {
                            material.isConsumed = true;
                        }
                        this.model.consumptionList.addModel(material);
                        return material;
                    });
                    return this.materialService.addMaterialsToLocalStorage(materials);
                }),
                tap(() => this.eventService.triggerMaterialPicked()),
                catchError((error) => {
                    this.log.error(
                        'Error in ConfirmationMaterialStockComponent -> pickupFromStock -> materialService.addMaterialToLocalStorage: ',
                        error,
                    );
                    return of(undefined);
                }),
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                this.isPickupInProgress = false;
                this.modalInstance.hide();
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
    }

    /**
     * Checks if any material selected for pickup
     *
     * @returns true if any material selected
     */
    public isMaterialSelected(): boolean {
        return this.model.cseMaterialStocks.some(
            (cseMaterialStock) => cseMaterialStock.quantity.value && cseMaterialStock.quantity.value > 0,
        );
    }

    /**
     * Pickup an item from stock
     */
    public pickupFromStock(): void {
        // The search criteria only matter for other stock; boot stock is pre-loaded without a search
        if (this.activeTab === MatStockTab.OTHER_STOCK && this.confirmationMaterialStockForm.invalid) {
            this.appNotificationService.setAppNotification(
                AppNotificationType.WARN,
                <string>this.translate.instant('CONFIRMATION_MATERIAL_CSE_STOCK_ADD_ERROR_NO_TITLE'),
                <string>this.translate.instant('CONFIRMATION_MATERIAL_CSE_STOCK_ERROR_NO_BODY'),
            );
            return;
        }
        if (this.model.cseMaterialStocks?.length <= 0) {
            return;
        }

        const selectedOtherStocks = this.model.otherStock.filter((material) => {
            const isSmartLockerMaterial = this.smartlockerService.isSmartlockerMaterial(
                material.storageLocationDesc.value,
            );
            return !isSmartLockerMaterial && material.quantity.value && material.quantity.value > 0;
        });

        this.materialStockService
            .checkIfMaterialExistsInBootStock(selectedOtherStocks, this.model.bootStock)
            .subscribe((continuePickup) => {
                if (!continuePickup) {
                    selectedOtherStocks.forEach((material) => {
                        material.quantity.value = 0;
                        material.quantity = assignModelOptions(
                            material.quantity,
                            { ...material.quantity.options },
                            material,
                        );
                    });

                    this.setActiveTab(MatStockTab.BOOT_STOCK);
                    return;
                }
                this.isPickupInProgress = true;
                const pickedMaterials = this.model.cseMaterialStocks.filter((material) => {
                    return material.quantity.value && material.quantity.value > 0;
                });
                if (this.configService.getEnableBillingIndicator()) {
                    this.updateMaterialsBillingIndicators(pickedMaterials)
                        .pipe(take(1))
                        .subscribe((materials) => this.pickUpMaterialStock$.next(materials));
                } else {
                    this.pickUpMaterialStock$.next(pickedMaterials);
                }
            });
    }

    public switchToBootStockTab(): void {
        this.setActiveTab(MatStockTab.BOOT_STOCK);
    }

    public onTabSelected(tab: MatStockTab): void {
        this.activeTab = tab;
        this.applyMaterialNumberRequirement();
        this.validateForm();
    }

    private setActiveTab(tab: MatStockTab): void {
        const tabSet = this.tabSet();
        if (tabSet) {
            tabSet.tabs[tab].active = true;
        }
        this.onTabSelected(tab);
    }

    private applyMaterialNumberRequirement(): void {
        const required =
            this.isMaterialNumberRequiredForSearch &&
            !this.isBootStockFromSideMenu &&
            this.activeTab === MatStockTab.OTHER_STOCK;
        this.model.materialNumber.options = { ...this.model.materialNumber.options, required };
        // The text input's validator reads options.required on each run, so re-run it for the new tab
        this.confirmationMaterialStockFormChildren.controls.forEach((control) =>
            control.updateValueAndValidity({ emitEvent: false }),
        );
    }

    /**
     * Search the stock based on FrontID, MaterialNumber and StorageLocation
     */
    public searchStock(): void {
        // check for form validation
        if (this.confirmationMaterialStockForm.invalid) {
            this.appNotificationService.setAppNotification(
                AppNotificationType.WARN,
                <string>this.translate.instant('CONFIRMATION_MATERIAL_CSE_STOCK_ADD_ERROR_NO_TITLE'),
                <string>this.translate.instant('CONFIRMATION_MATERIAL_CSE_STOCK_ERROR_NO_BODY'),
            );
            return;
        }

        this.isSearchInProgress = true;
        this.materialStockService
            .searchMaterialStock(this.model)
            .pipe(
                tap(([bootStock, otherStock]) => {
                    this.model.updateMaterialStock(bootStock, otherStock);
                    this.filteredBootStock = this.getFilteredMaterials(this.model.bootStock);

                    if (this.filteredBootStock.length === 0 && this.model.otherStock.length > 0) {
                        this.setActiveTab(MatStockTab.OTHER_STOCK);
                    }
                }),
                catchError((error) => {
                    this.log.error('Error', JSON.stringify(error, Object.getOwnPropertyNames(error)));
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('CONFIRMATION_MATERIAL_CSE_OTHER_STOCK_SEARCH_ERROR_TITLE'),
                        <string>this.translate.instant('CONFIRMATION_MATERIAL_CSE_OTHER_STOCK_SEARCH_ERROR_BODY'),
                    );
                    return of(undefined);
                }),
            )
            .subscribe(() => {
                this.isSearchInProgress = false;
                this.changeDetector.detectChanges();
            });
    }

    private loadBootStock(): void {
        this.isBootStockLoading = true;
        this.materialStockService
            .loadBootStock(this.model.notification, this.model.consumptionList)
            .pipe(
                tap((bootStock) => {
                    this.model.bootStock = bootStock;
                    this.filteredBootStock = this.getFilteredMaterials(bootStock);
                }),
                catchError((error) => {
                    this.log.error('Error', JSON.stringify(error, Object.getOwnPropertyNames(error)));
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('MATERIAL_REQUEST_ERROR_TITLE'),
                        <string>this.translate.instant('MATERIAL_REQUEST_ERROR_BODY'),
                    );
                    return of(undefined);
                }),
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                this.isBootStockLoading = false;
                this.changeDetector.detectChanges();
            });
    }

    public validateForm(): void {
        this.filteredBootStock = this.getFilteredMaterials(this.model.bootStock);
        this.changeDetector.detectChanges();
    }

    private updateMaterialsBillingIndicators(pickedMaterials: MaterialModel[]): Observable<MaterialModel[]> {
        const notification = this.model.notification;
        if (typeof notification === 'undefined') {
            return of([]);
        }
        const materialBillingModel = pickedMaterials.map(
            (material) =>
                new MaterialBillableChangeableModel(
                    material.materialIdentifier.materialNumber.value,
                    '',
                    '',
                    material.quantity.value?.toString(),
                ),
        );
        const billingIndicatorModel = new MaterialBillingIndicatorModel(
            notification.identifier.getQmnum(),
            notification.identifier.getManum(),
            notification.taskGroup,
            materialBillingModel,
        );
        return this.cseStockService.updateMatBillingIndicator(pickedMaterials, billingIndicatorModel);
    }

    /**
     * Client-side narrowing of the boot stock: the full van inventory is already loaded up front,
     * so material number, description and storage location only filter what's loaded rather than
     * gate a query like searchStock() does for other stock.
     */
    public getFilteredMaterials(materials: MaterialModel[]): MaterialModel[] {
        const materialNumberFilter = this.model.materialNumber.value;
        const descriptionFilter = this.model.materialDescription.value;
        const storageLocationFilter = this.model.storageLocation.value;

        const getFilterResult = (searchableTerm: string, searchTerm: string): boolean => {
            if (!searchTerm) {
                return true; // empty filter should not exclude anything
            }

            return searchableTerm.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase());
        };

        return materials
            .filter((material) =>
                getFilterResult(material.materialIdentifier.materialNumber.value, materialNumberFilter),
            )
            .filter((material) => getFilterResult(material.description.value, descriptionFilter))
            .filter((material) => getFilterResult(material.storageLocation.value, storageLocationFilter));
    }
}
